import React from 'react'

import { NotificationListInfoFragment } from '@sketch/gql-types'
import { thumbnailForPreviewFile } from '@sketch/modules-common'

import { ReactComponent as DownloadIcon } from '@sketch/icons/arrow-down-to-line-16'
import { ReactComponent as AtIcon } from '@sketch/icons/at-email-16'
import { ReactComponent as ChatBubbleIcon } from '@sketch/icons/chat-bubble-16'
import { ReactComponent as CheckmarkIcon } from '@sketch/icons/checkmark-16'
import { ReactComponent as ArchiveBoxIcon } from '@sketch/icons/archive-box-16'
import { ReactComponent as TrashIcon } from '@sketch/icons/trash-empty-prism-16'
import { ReactComponent as MailEnvelopeIcon } from '@sketch/icons/mail-envelope-16'
import { ReactComponent as StarIcon } from '@sketch/icons/star-16'
import {
  FolderPreview,
  ImagePreview,
  PersonPreview,
  WorkspacePreview,
} from '../InboxListItem'

export type NotificationTypename =
  | 'AddShareMemberNotification'
  | 'CommentNotification'
  | 'DownloadableAssetRenderedNotification'
  | 'FolderArchivedNotification'
  | 'FolderTrashedNotification'
  | 'FolderPermanentlyDeletedNotification'
  | 'MentionNotification'
  | 'StarNotification'
  | 'WorkspaceInviteAcceptedNotification'
  | 'WorkspaceInvitedNotification'

export type NotificationTitle =
  | string
  | ((notification: NotificationListInfoFragment) => string)

export type NotificationConfig = {
  title: NotificationTitle
  renderListItemPreview: (
    notification: NotificationListInfoFragment
  ) => React.ReactNode
  typeIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  reason: React.ReactNode
}

export const resolveNotificationTitle = (
  title: NotificationTitle,
  notification: NotificationListInfoFragment
): string => (typeof title === 'function' ? title(notification) : title)

const REASON_DOCUMENT = (
  <>
    You received this notification because{' '}
    <strong>you follow this document</strong>
  </>
)

const REASON_THREAD = (
  <>
    You received this notification because{' '}
    <strong>you follow this thread</strong>
  </>
)

const REASON_MENTION = (
  <>
    You received this notification because <strong>you were mentioned</strong>
  </>
)

const REASON_ASSETS = (
  <>
    You received this notification because <strong>you requested assets</strong>
  </>
)

const REASON_WORKSPACE_INVITE = (
  <>
    You received this notification because{' '}
    <strong>you were invited to join this workspace</strong>
  </>
)

const REASON_WORKSPACE_INVITE_ACCEPTED = (
  <>
    You received this notification because{' '}
    <strong>someone you invited joined this workspace</strong>
  </>
)

const REASON_SHARE_INVITE = (
  <>
    You received this notification because <strong>you were invited</strong>
  </>
)

const REASON_FOLDER_SHARED = (
  <>
    You received this notification because{' '}
    <strong>this folder was shared with you</strong>
  </>
)

const workspacePreview = (notification: NotificationListInfoFragment) => (
  <WorkspacePreview
    name={notification.workspace?.name}
    src={notification.workspace?.avatar?.small}
  />
)

const sharePreview = workspacePreview

/**
 * Renders the document thumbnail from a version's `previewFile`, falling back
 * to the share/workspace avatar when there's no preview (or no share). Shared
 * by every notification type that surfaces a document thumbnail. The `share` is
 * passed in by the caller, where the notification union is already narrowed.
 */
const documentThumbnailPreview = (
  notification: NotificationListInfoFragment,
  share: { name: string } | null | undefined,
  previewFile: Parameters<typeof thumbnailForPreviewFile>[0]
) => {
  const thumbnail = thumbnailForPreviewFile(previewFile)
  if (!thumbnail || !share) {
    return workspacePreview(notification)
  }

  return <ImagePreview src={thumbnail} alt={`Preview of ${share.name}`} />
}

const commentMentionPreview = (notification: NotificationListInfoFragment) => {
  if (
    notification.__typename !== 'CommentNotification' &&
    notification.__typename !== 'MentionNotification'
  ) {
    return sharePreview(notification)
  }

  // Temporary disable the thumbnail fetching because of a production issue
  // https://sketch.slack.com/archives/C04851ZU8FM/p1783086250683029?thread_ts=1783006390.487219&cid=C04851ZU8FM
  //
  // return documentThumbnailPreview(
  //   notification,
  //   notification.share,
  //   notification.share?.version?.document?.previewFile
  // )
}

const downloadableAssetPreview = (
  notification: NotificationListInfoFragment
) => {
  if (notification.__typename !== 'DownloadableAssetRenderedNotification') {
    return sharePreview(notification)
  }

  return documentThumbnailPreview(
    notification,
    notification.share,
    notification.notifiedVersion?.document?.previewFile
  )
}

const starPreview = (notification: NotificationListInfoFragment) => {
  if (notification.__typename !== 'StarNotification') {
    return sharePreview(notification)
  }

  // return documentThumbnailPreview(
  //   notification,
  //   notification.share,
  //   notification.share?.version?.document?.previewFile
  // )
}

const inviterPreview = (notification: NotificationListInfoFragment) => (
  <PersonPreview
    name={notification.user?.name}
    src={notification.user?.avatar?.small}
  />
)

const folderPreview = (notification: NotificationListInfoFragment) => {
  // Archived and trashed folders still exist, so we can show their contents.
  // A permanently-deleted folder is gone (the notification carries no `folder`),
  // so it falls through to the workspace avatar.
  if (
    notification.__typename === 'FolderArchivedNotification' ||
    notification.__typename === 'FolderTrashedNotification'
  ) {
    return <FolderPreview folder={notification.folder} />
  }

  return workspacePreview(notification)
}

const CONFIG: Record<NotificationTypename, NotificationConfig> = {
  CommentNotification: {
    title: 'New comment',
    renderListItemPreview: commentMentionPreview,
    typeIcon: ChatBubbleIcon,
    reason: REASON_THREAD,
  },
  MentionNotification: {
    title: 'New mention',
    renderListItemPreview: commentMentionPreview,
    typeIcon: AtIcon,
    reason: REASON_MENTION,
  },
  AddShareMemberNotification: {
    title: notification =>
      notification.user?.name
        ? `${notification.user.name} invited you to this document`
        : 'Document access invite',
    renderListItemPreview: inviterPreview,
    typeIcon: CheckmarkIcon,
    reason: REASON_SHARE_INVITE,
  },
  DownloadableAssetRenderedNotification: {
    title: 'Your assets are ready to download',
    renderListItemPreview: downloadableAssetPreview,
    typeIcon: DownloadIcon,
    reason: REASON_ASSETS,
  },
  FolderArchivedNotification: {
    title: 'Folder archived',
    renderListItemPreview: folderPreview,
    typeIcon: ArchiveBoxIcon,
    reason: REASON_FOLDER_SHARED,
  },
  FolderTrashedNotification: {
    title: 'Folder moved to Trash',
    renderListItemPreview: folderPreview,
    typeIcon: TrashIcon,
    reason: REASON_FOLDER_SHARED,
  },
  FolderPermanentlyDeletedNotification: {
    title: 'Folder permanently deleted',
    renderListItemPreview: folderPreview,
    typeIcon: TrashIcon,
    reason: REASON_FOLDER_SHARED,
  },
  StarNotification: {
    title: notification =>
      notification.user?.name
        ? `${notification.user.name} starred a new version`
        : 'New starred version',
    renderListItemPreview: starPreview,
    typeIcon: StarIcon,
    reason: REASON_DOCUMENT,
  },
  WorkspaceInvitedNotification: {
    title: notification =>
      notification.user?.name
        ? `${notification.user.name} invited you to a workspace`
        : 'Workspace invite',
    renderListItemPreview: workspacePreview,
    typeIcon: MailEnvelopeIcon,
    reason: REASON_WORKSPACE_INVITE,
  },
  WorkspaceInviteAcceptedNotification: {
    title: notification =>
      notification.user?.name
        ? `${notification.user.name} accepted your workspace invitation`
        : 'Workspace invitation accepted',
    renderListItemPreview: inviterPreview,
    typeIcon: CheckmarkIcon,
    reason: REASON_WORKSPACE_INVITE_ACCEPTED,
  },
}

const FALLBACK: NotificationConfig = {
  title: 'Notification',
  renderListItemPreview: workspacePreview,
  typeIcon: ChatBubbleIcon,
  reason: REASON_THREAD,
}

export const getNotificationConfig = (
  typename: string | undefined
): NotificationConfig => {
  if (typename && typename in CONFIG) {
    return CONFIG[typename as NotificationTypename]
  }
  return FALLBACK
}
